/**
 * Presentation for the Hybrid-parcel badge and the Portless-label state.
 *
 * The RULE is not here — it lives in `@threadheads/shared`
 * (`classifyPortlessFulfilment`), shared with the manifest partition and the
 * packing-station sticker gate. This module only maps an already-classified value
 * to copy + a badge variant, so the surfaces can't drift in what they *say* while
 * agreeing on what they *mean*.
 *
 * Four call sites: the Order Queue row, the Production Tracker's Orders table and
 * its batch-expansion row, and the packing station.
 *
 * Returns tokens rather than JSX: all four surfaces live under `ProductionLayout`
 * (App.tsx — `/production/orders`, `/production/tracker`, the packing station) and so
 * all four follow PRODUCTION_DESIGN.md, but a dense coordinator list row and a
 * full-width packing slip are worlds apart in type scale. Each call site renders at
 * its own size.
 *
 * Colour comes from `badge.tsx`'s existing CVA variants, never hand-rolled Tailwind.
 * The class trios this file used to carry were byte-identical to those variants, and
 * their `dark:` halves were dead code — nothing in the app ever adds the `dark`
 * class, and DESIGN.md §16 defers dark mode.
 *
 * `info` (blue), NOT violet: violet already means "Mixed POD/PAP" in this domain
 * (ClientOrderTypeBadge, TrackerBatchTable) and sits six hue-degrees from the brand
 * purple of ClientOrgChip — which appears on the SAME row at a shared 3PL floor.
 * Blue is PRODUCTION_DESIGN.md §4.1's "information / metadata", which is what a
 * parcel property is.
 *
 * Blue alone was not enough, hence the GLYPH. The POD pill 12px away on the Order
 * Queue row is also `variant="info" size="sm"` with no icon (ExpandableOrderRow), as
 * is the POD fulfilment pill on the Tracker's Orders table — so colour + size left
 * ten pixels of text as the only difference between "this order prints on demand"
 * and "this parcel is deliberately half-empty". The icon lives on the descriptor,
 * not at the call sites, so the three of them cannot drift.
 */
import { PackageCheck, PackageOpen, type LucideIcon } from 'lucide-react';
import type { PortlessFulfilmentType } from '@threadheads/shared';
import type { BadgeProps } from '@/components/ui/badge';

type Descriptor = {
  /** i18n key in the `production` namespace. */
  labelKey: string;
  /**
   * i18n key for the full explanation.
   *
   * Rendered as VISIBLE copy wherever the surface has room (the packing slip's
   * callout, the Tracker's expanded order row) — never as a `title` attribute that
   * is the only carrier of it. PRODUCTION_DESIGN.md §10 bans tooltips holding
   * critical info: these stations are tablets, and hover does not exist there. On
   * the dense rows that cannot fit a sentence, the badge label IS the whole message.
   *
   * (The i18n keys keep their historical `…Tooltip` names; only the role changed.)
   */
  descriptionKey: string;
  /**
   * A `badge.tsx` CVA variant name — never a raw class string. Typed off `BadgeProps`
   * rather than hand-listing the names, so renaming a variant in badge.tsx fails the
   * build here instead of silently falling through to the `default` styling.
   */
  variant: NonNullable<BadgeProps['variant']>;
  /**
   * The glyph that separates this pill from its identically-coloured neighbours.
   * Both of Hybrid's siblings on the Order Queue row already carry one (PackageX for
   * blocked, PackageCheck for label-ready), and `PackageOpen` is already the packing
   * slip's glyph for this same concept, so the pair reads as one idea across
   * surfaces. Render it `aria-hidden` — the meaning is in the visible label, never in
   * the icon alone (PRODUCTION_DESIGN.md §13, colour/icon is never the only cue).
   */
  icon: LucideIcon;
};

/**
 * There is deliberately NO Cross Dock descriptor. Cross Dock is the ABSENCE of the
 * badge: `classifyPortlessFulfilment` returns 'crossdock' for any order with no
 * matching tag, so badging it asserted "the whole parcel is cross-docked to
 * Portless" on every StarShipIt order on every non-Portless floor. Every other pill
 * on these rows is exception-only for the same reason.
 */
const HYBRID: Descriptor = {
  labelKey: 'portlessFulfilment.hybrid',
  descriptionKey: 'portlessFulfilment.hybridTooltip',
  variant: 'info',
  icon: PackageOpen,
};

const LABEL_READY: Descriptor = {
  labelKey: 'portlessFulfilment.labelReady',
  descriptionKey: 'portlessFulfilment.labelReadyTooltip',
  variant: 'success',
  icon: PackageCheck,
};

/** The Hybrid badge, or `null` for Cross Dock — which gets no badge at all. */
export function hybridBadge(type: PortlessFulfilmentType): Descriptor | null {
  return type === 'hybrid' ? HYBRID : null;
}

/**
 * The "Portless label ready" descriptor.
 *
 * A plain constant, not a function: the previous `labelStateBadge()` took a
 * `shipsPortless` flag that its only caller hardcoded to `true`, so its null-return
 * gate never ran, its `labelWaiting` branch was unreachable, and the caller's
 * `?? fulfilment` fallback was dead — it would have rendered the Hybrid label under
 * a "label ready" icon. The real gate lives at the render site.
 */
export const labelReadyBadge: Descriptor = LABEL_READY;
